import { parseOffset, toAbsoluteOffset, toRelativeOffset } from "@artis/sdk";

import type { Cell } from "@package/scratchpad-utils";
import { type ScannedCall, scanCalls } from "./scan";

export type OffsetClassification = {
  hasError: boolean;
  hasRelative: boolean;
  hasAbsolute: boolean;
};

export type PeriodTarget = "absolute" | "relative";

/** Index of the first offset argument, per function that takes offsets. */
const FIRST_OFFSET_ARG: Record<string, number> = { CURVE: 2, PERIOD: 0 };

type OffsetArg = { start: number; end: number; text: string };

function argSlices(call: ScannedCall): { argIndex: number; start: number; end: number }[] {
  return call.argStarts.map((start, argIndex) => {
    const end =
      argIndex + 1 < call.argStarts.length ? call.argStarts[argIndex + 1]! - 1 : call.end - (call.closed ? 1 : 0);
    return { argIndex, start, end };
  });
}

/** The offset arguments of every CURVE and PERIOD call: the text the grammar should read, with the span to splice. */
function curveOffsetArgs(src: string): OffsetArg[] {
  const args: OffsetArg[] = [];
  for (const call of scanCalls(src)) {
    const firstOffsetArg = FIRST_OFFSET_ARG[call.name?.toUpperCase() ?? ""];
    if (firstOffsetArg === undefined) continue;
    const quoted = new Map(call.strings.map((s) => [s.argIndex, s]));
    for (const { argIndex, start, end } of argSlices(call)) {
      if (argIndex < firstOffsetArg) continue;
      const string = quoted.get(argIndex);
      if (string) {
        args.push({ start: string.start, end: string.end, text: string.value });
        continue;
      }
      const raw = src.slice(start, end);
      const text = raw.trim();
      if (text === "") continue;
      const from = start + (raw.length - raw.trimStart().length);
      args.push({ start: from, end: from + text.length, text });
    }
  }
  return args.sort((a, b) => a.start - b.start);
}

export function curveOffsetTexts(src: string): string[] {
  return curveOffsetArgs(src).map((arg) => arg.text);
}

export function classifyOffsets(cells: readonly Pick<Cell, "src" | "error">[]): OffsetClassification {
  const result = { hasError: false, hasRelative: false, hasAbsolute: false };
  for (const cell of cells) {
    if (cell.error) result.hasError = true;
    for (const text of curveOffsetTexts(cell.src)) {
      const { parsed } = parseOffset(text);
      if (parsed?.kind === "relative") result.hasRelative = true;
      if (parsed?.kind === "absolute") result.hasAbsolute = true;
    }
  }
  return result;
}

/**
 * Rewrite every CURVE and PERIOD offset in a formula to the target kind, leaving offsets already
 * of that kind, unreadable offsets, and unconvertible ones (past periods) as written.
 */
export function convertPeriods(src: string, target: PeriodTarget): string {
  const args = curveOffsetArgs(src);
  let out = src;
  for (let i = args.length - 1; i >= 0; i--) {
    const { start, end, text } = args[i]!;
    const { parsed } = parseOffset(text);
    if (!parsed || parsed.kind === target) continue;
    const { normalized } = target === "absolute" ? toAbsoluteOffset(text) : toRelativeOffset(text);
    if (!normalized) continue;
    out = `${out.slice(0, start)}"${normalized}"${out.slice(end)}`;
  }
  return out;
}
